<template>
<div id="user" v-if="user">
  <h3>
    User #{{ userId }}
    <span
      class="badge badge-success"
      v-if="user.active_subscriber"
    >
      Subscriber
    </span>
  </h3>

  <hr />

  <user-nav
    :userId="userId"
  />

  <h4>
    User Details

    <router-link
      class="btn btn-primary float-right mb-2"
      :to="{
        name: 'author_puzzles',
        userId: userId
      }"
      v-if="isAuthor"
    >
      Puzzles
    </router-link>
  </h4>

  <table class="table">
    <tr>
      <td>
        Name
      </td>
      <td>
        {{ user.name || 'n/a' }}
        <span
          v-if="user.staff"
        >
          <span
            class="badge badge-sm badge-info"
          >
            Staff
          </span>
        </span>
        <span
          v-if="user.verified"
        >
          <span
            class="badge badge-sm badge-primary"
          >
            Verified
          </span>
          <button
            type="submit"
            class="btn btn-sm btn-primary"
            v-on:click.prevent="unverify"
            v-if="$root.isAdmin"
          >
            Unverify
            <spinner
              size="small"
              v-if="loaders.verifyUser"
            />
          </button>
        </span>
        <span
          v-else
        >
          <button
            type="submit"
            class="btn btn-sm btn-primary"
            v-on:click.prevent="verify"
            v-if="$root.isAdmin"
          >
            Verify
            <spinner
              size="small"
              v-if="loaders.verifyUser"
            />
          </button>
        </span>
      </td>
    </tr>
    <tr>
      <td>
        Username
      </td>
      <td>
        {{ user.username }}
      </td>
    </tr>
    <tr>
      <td>
        Email
      </td>
      <td>
        {{ user.email }}
        <span
          v-if="user.email_suspended"
        >
          <span
            class="badge badge-sm badge-danger"
          >
            Suspended
          </span>
          <button
            type="button"
            class="btn btn-success btn-sm"
            v-on:click="reinstateEmail"
          >
            Reinstate
            <spinner size="small" v-if="loading"></spinner>
          </button>
        </span>
        <button
          type="button"
          class="btn btn-danger btn-sm"
          v-on:click="confirmEmailSuspension"
          v-else
        >
          Suspend
          <spinner size="small" v-if="loading"></spinner>
        </button>
      </td>
    </tr>
    <tr
      v-if="user.email_suspension_reason"
    >
      <td>
        Email Suspension Reason
        <div
          v-if="last_errored_record"
        >
          {{ user.last_errored_record }}
        </div>

      </td>
      <td
        class="alert alert-danger"
      >
        {{ user.email_suspension_reason }}
      </td>
    </tr>
    <tr>
      <td>
        Joined At
      </td>
      <td>
        {{ this.formatDate(user.created_at) }}
      </td>
    </tr>
    <tr>
      <td>
        Updated On
      </td>
      <td>
        {{ this.formatDate(user.updated_at) }}
      </td>
    </tr>
    <tr>
      <td>
        Active for Apps?
      </td>
      <td>
        <span
          class="badge badge-primary badge-sm"
        >
          {{ user.active ? 'Yes' : 'No' }}
        </span>
      </td>
    </tr>
    <tr>
      <td>
        Visible to Others?
      </td>
      <td>
        <span
          class="badge badge-primary badge-sm"
        >
          {{ user.private ? 'No' : 'Visible' }}
        </span>
      </td>
    </tr>
    <tr>
      <td>
        Active Subscriber?
      </td>
      <td>
        <span
          class="badge badge-primary"
          v-if="user.active_subscriber"
        >
          Yes
        </span>
        <span
          class="badge badge-danger"
          v-else
        >
          No
        </span>
      </td>
    </tr>
    <tr>
      <td>
        Receives Targeted Emails?
      </td>
      <td>
        <span
          class="badge badge-primary"
          v-if="user.receive_targeted_emails"
        >
          Yes
        </span>
        <span
          class="badge badge-danger"
          v-else
        >
          No
        </span>
      </td>
    </tr>
    <tr>
      <td>
        Role
      </td>
      <td
        v-if="user.role"
      >
        <span
          class="badge badge-info"
        >
          {{ user.role.attributes.name }}
        </span>
        <span
          class="badge badge-warning"
          v-if="user.featured"
        >
          Featured
        </span>
      </td>
    </tr>
    <tr>
      <td>
        Requested Password Reset
      </td>
      <td>
        <p
          class="alert alert-info text-center"
          v-if="user.password_reset_requested"
        >
          The user has requested a reset, but has not confirmed it.
        </p>

        <boolean-badge
          :badgeVal="user.password_reset_requested"
          v-if="!user.password_reset_requested"
        />

        <a
          :href="user.full_reset_url"
          class="btn btn-sm btn-secondary"
          v-if="user.password_reset_requested"
          v-on:click.prevent="copyPasswordResetUrl"
        >
          Copy Password Reset URL
        </a>

        <request-password-reset-button
          :email="user.email"
          ref="requestPasswordResetButton"
          v-if="user.email"
        />
      </td>
    </tr>
    <tr
      v-if="user.last_password_reset_requested_at"
    >
      <td>
        Password Reset Last Requested At
      </td>
      <td>
        {{
          this.formatDate(
            user.last_password_reset_requested_at
          )
        }}
      </td>
    </tr>
    <tr
      v-if="user.current_password_reset_count"
    >
      <td>
        Password Reset Request Count
      </td>
      <td>
        {{ user.current_password_reset_count }}
      </td>
    </tr>
  </table>

  <user-detail
    :header="userDetailHeader"
    :userId="userId"
    :userUserDetail="user.user_detail"
    v-if="user.user_detail"
  />

  <div
    class="invitations"
    v-if="user.invited_at"
  >
    <hr />

    <h3>User Invitation Details</h3>

    <table class="table">
      <tr>
        <td>
          Invited At
        </td>
        <td>
          {{ this.formatDate(user.invited_at) }}
        </td>
      </tr>
      <tr>
        <td>
          Invited By
        </td>
        <td>
          <router-link :to="{
            name: 'user',
            params: {
              userId: inviter.id
            }
          }">
            {{ user.inviter.attributes.name }}
          </router-link>
        </td>
      </tr>
      <tr>
        <td>
          Invitation Accepted At
        </td>
        <td>
          {{ this.formatDate(user.invitation_accepted_at) }}
        </td>
      </tr>

    </table>
  </div>

  <hr />

  <purchases
    v-bind:userId="userId"
  />

  <hr />

  <h3>Purchased Subscriptions</h3>

  <subscriptions
    no-nav
    no-search
    v-bind:userId="userId"
  />

  <hr />

  <h3>Newsletter Subscriptions</h3>

  <newsletter-type-subscriber-key />
  <paged-newsletter-subscriptions
    :pageItems="newsletter_type_subscriptions"
  >
    <template
      slot="paged_item"
      slot-scope="item"
    >
      <router-link
        :class="$root.listItemClasses + ' ' + subscriptionClass(item.status)"
        :to="{
          name: 'newsletter_type_subscriber',
          params: {
            newsletterTypeSubscriberId: item.id
          }
        }"
      >
        {{ item.name }}
      </router-link>
    </template>
  </paged-newsletter-subscriptions>

  <hr />

  <h3>Unsubscribed Puzzle Types</h3>

  <p>
    If a user has unsubscribed from these puzzle types,
    they will no longer receive newsletters containing puzzles assigned
    to that puzzle type.
  </p>

  <paged-unsubscribed-providers
    :pageItems="user.unsubscribed_providers"
  >
    <template
      slot="paged_item"
      slot-scope="item"
    >
      <router-link
        :class="$root.listItemClasses + ' list-group-item-danger'"
        :to="{
          name: 'provider',
          params: {
            providerId: item.id
          }
        }"
      >
        {{ item.title }}
      </router-link>
    </template>
  </paged-unsubscribed-providers>

  <hr />

  <h3>Newsletter Delivery History</h3>

  <user-newsletter-delivery-history
    :userId="userId"
    v-if="newsletter_type_subscriptions.length"
  />
  <p
    v-else
  >
    No newsletter subscriptions found.
  </p>
</div>
</template>

<script>
import Pagination from 'sai-vue-kaminari'
import Spinner from 'vue-simple-spinner'

import BooleanBadge from '@/components/utils/BooleanBadge'

import UserNewsletterDeliveryHistory from '@/components/UserNewsletterDeliveryHistory'
import NewsletterTypeSubscriberKey from '@/components/NewsletterTypeSubscriberKey'
import NewsletterTypeSubscriberItems from '@/components/NewsletterTypeSubscriberItems'
import RequestPasswordResetButton from '@/components/RequestPasswordResetButton'
import Subscriptions from '@/components/Subscriptions'
import UserDetail from '@/components/UserDetail'
import UserPurchases from '@/components/UserPurchases'
import UserNav from '@/components/UserNav'

import { mapState } from 'vuex'

import UserStore from '@/stores/UserStore'

export default {
  data () {
    let state = UserStore.state
    state.loading = false

    return state
  },

  props: {
    'userId': {
      type: [ Number, String ],
      default: undefined
    }
  },

  components: {
    'boolean-badge': BooleanBadge,
    'newsletter-type-subscriber-key': NewsletterTypeSubscriberKey,
    'newsletter-type-subscriber-items': NewsletterTypeSubscriberItems,
    'paged-newsletter-subscriptions': Pagination,
    'paged-unsubscribed-providers': Pagination,
    'purchases': UserPurchases,
    'request-password-reset-button': RequestPasswordResetButton,
    'spinner': Spinner,
    'subscriptions': Subscriptions,
    'user-detail': UserDetail,
    'user-nav': UserNav,
    'user-newsletter-delivery-history': UserNewsletterDeliveryHistory
  },

  computed: mapState({
    base: state => state.base,
    loaders: state => state.LoaderModule.loaders,
    user: state => state.UserModule.user,

    isAuthor () {
      return this.user.role.attributes.alias === 'author'
    },

    userDetailHeader () {
      return `${this.user.role.attributes.name} Profile`
    }
  }),

  mounted () {
    this.get().then(() => {
      this.$refs.requestPasswordResetButton.$on(
        'onPasswordResetSuccess',
        this.get
      )
    })
  },

  methods: {
    formatDate (d) {
      return this.$root.displayDateTimeFmt(d)
    },

    get () {
      return this.$store.dispatch(
        `UserModule/getUser`, this.userId
      ).then(() => {
        this.getNewsletterTypes().then((newsletterTypesObj) =>
          this.parseNewsletterSubscriptions(newsletterTypesObj)
        )
      }).catch(this.$root.processErrors)
    },

    getNewsletterTypes () {
      this.newsletter_types = {}
      return this.apiGet(
        '/newsletter_types'
      ).then((newsletterTypesResp) => {
        return newsletterTypesResp.map((newsletterType) => {
          this.newsletter_types[newsletterType.id] = newsletterType
        })
      })
    },

    parseNewsletterSubscriptions (newsletterTypesObj) {
      this.newsletter_type_subscriptions = this.user.newsletter_type_subscriptions.map((item) => {
        let id = item.relationships.newsletter_type.id
        let subscr = this.newsletter_types[id]
        subscr.id = item.id
        subscr.status = item.status

        return subscr
      })
    },

    confirmEmailSuspension () {
      if (
        confirm(
          'Are you sure you want to suspend this email? This user will no longer receive any emails.'
        )
      ) {
        this.suspendEmail()
      }
    },

    suspendEmail () {
      const msg = prompt(
        'Please enter the reason why you are suspending the user below.'
      )

      if (msg) {
        this.loading = true
        this.$http.post(
          this.apiUrl(
            '/users/' + this.userId + '/suspend_email', true
          ), {
            email_suspension_reason: msg
          }
        ).then(this.get).finally(() => {
          this.loading = false
        })
      } else {
        if (
          confirm(
            'You must enter a reason as to why you are suspending this user, or you can cancel the suspension request.'
          )
        ) {
          this.suspendEmail()
        }
      }
    },

    reinstateEmail () {
      if (
        confirm(
          "Are you sure you want to re-instate this user's email address?"
        )
      ) {
        this.loading = true
        this.$http.delete(
          this.apiUrl(
            '/users/' + this.userId + '/suspend_email', true
          )
        ).then(this.get).finally(() => {
          this.loading = false
        })
      }
    },

    copyPasswordResetUrl (ev) {
      navigator.clipboard.writeText(this.user.full_reset_url).then(() => {
        const priorText = ev.target.innerText
        ev.target.innerText = 'Copied!'

        window.setTimeout(() => {
          ev.target.innerText = priorText
        }, 5000)
      }, () => {
        alert('Could not copy reset URL')
      })
    },

    subscriptionClass (status) {
      let className

      switch (status) {
        case 'accepted':
          className = 'success'
          break
        case 'pending':
          className = 'primary'
          break
        case 'unsubscribed':
          className = 'danger'
          break
      }

      return 'list-group-item-' + className
    },

    verify () {
      if (!this.user.name) {
        alert('A name is required to verify this user.')
      } else {
        this.$store.dispatch(
          `UserModule/verifyUser`, this.userId
        ).catch(this.$root.processErrors)
      }
    },

    unverify () {
      this.$store.dispatch(
        `UserModule/unverifyUser`, this.userId
      ).catch(this.$root.processErrors)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/User.vue